import { useState, useEffect } from "react";
import { Play } from "lucide-react";

const buildLevels = (video) => {
  if (video.thumbnail) return [video.thumbnail];
  if (video.youtube_id) {
    const id = video.youtube_id;
    return [
      `https://img.youtube.com/vi/${id}/maxresdefault.jpg`, // best, HD uploads only
      `https://img.youtube.com/vi/${id}/sddefault.jpg`,
      `https://img.youtube.com/vi/${id}/hqdefault.jpg`, // always exists
      `https://img.youtube.com/vi/${id}/mqdefault.jpg`,
    ];
  }
  return [];
};

export const ytThumb = (id, custom) => custom || (id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : null);

export const VideoCard = ({ video, featured = false }) => {
  const levels = buildLevels(video);
  const isCustom = !!video.thumbnail;
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    setIdx(0);
  }, [video.id, video.youtube_id, video.thumbnail]);

  const src = levels[idx] || null;
  const href = video.youtube_id ? `https://www.youtube.com/watch?v=${video.youtube_id}` : null;

  const handleError = () => {
    if (idx < levels.length - 1) setIdx(idx + 1);
  };
  const handleLoad = (e) => {
    // YouTube serves a 120x90 grey placeholder (HTTP 200) when a size is missing — step down a level.
    if (!isCustom && e.target.naturalWidth <= 120 && idx < levels.length - 1) setIdx(idx + 1);
  };

  const Inner = (
    <div className={`group card-surface overflow-hidden ${featured ? "" : "h-full"}`} data-testid={`video-card-${video.id}${featured ? "-featured" : ""}`}>
      <div className="relative aspect-video overflow-hidden">
        {src ? (
          <img
            src={src}
            alt={video.title}
            onError={handleError}
            onLoad={handleLoad}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-brand-elevated to-brand-surface grid place-items-center">
            <span className="text-brand-textMuted text-xs uppercase tracking-widest">Video coming soon</span>
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-brand-base/70 to-transparent pointer-events-none" />
        <div className="absolute inset-0 grid place-items-center">
          <span className="h-16 w-16 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 grid place-items-center transition-all duration-300 group-hover:bg-brand-cyan group-hover:border-brand-cyan group-hover:scale-110">
            <Play size={24} className="fill-current text-white ml-1 group-hover:text-brand-base" />
          </span>
        </div>
      </div>
      <div className="p-5">
        <h3 className={`font-display font-semibold text-white leading-snug ${featured ? "text-xl" : "text-base"}`}>
          {video.title}
        </h3>
        {video.description && (
          <p className="mt-2 text-brand-textSec text-sm leading-relaxed line-clamp-2">{video.description}</p>
        )}
      </div>
    </div>
  );

  return href ? <a href={href} target="_blank" rel="noreferrer">{Inner}</a> : Inner;
};
